import { useEffect, useRef, useState } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";
import { MdChevronRight } from "react-icons/md";
import Tooltip from "components/tooltip";
import { useAuth } from "hooks/useAuth";

// Top-level items a Pathways (free tier) customer can open; the rest render locked.
const PATHWAY_ALLOWED_ROUTES = ["Pathways", "Settings"];

const toPath = (route) => `${route.layout || ""}${route.path || ""}`;
const visibleItems = (route) => (route.items || []).filter((item) => !item.hideInSidebar);
const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, "-");

/** Groups consecutive top-level routes that share a `section`. */
function groupBySection(routes) {
  const groups = [];
  routes.forEach((route) => {
    const last = groups[groups.length - 1];
    if (last && last.section === route.section) {
      last.routes.push(route);
    } else {
      groups.push({ section: route.section, routes: [route] });
    }
  });
  return groups;
}

function CollapsibleChildren({ id, isOpen, children }) {
  const contentRef = useRef(null);
  const previousOpen = useRef(isOpen);
  const [height, setHeight] = useState(isOpen ? "auto" : "0px");

  useEffect(() => {
    // The initial height is already correct; animate only real toggles.
    if (previousOpen.current === isOpen) return undefined;
    previousOpen.current = isOpen;
    const h = contentRef.current?.scrollHeight || 0;
    setHeight(`${h}px`);
    if (isOpen) {
      const timer = setTimeout(() => setHeight("auto"), 200);
      return () => clearTimeout(timer);
    }
    // Start from an explicit height so the collapse animates from `auto`.
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setHeight("0px"));
    });
    return () => cancelAnimationFrame(frame);
  }, [isOpen]);

  return (
    <div
      id={id}
      ref={contentRef}
      style={{
        height,
        overflow: "hidden",
        // Hidden after the animation so collapsed links leave the tab order.
        visibility: isOpen ? "visible" : "hidden",
        transition: "height 200ms ease-in-out, visibility 200ms",
      }}
    >
      {children}
    </div>
  );
}

/**
 * Sidebar navigation built from the route table: section labels, top-level
 * modules with an icon, and expandable child pages.
 *
 * @param {object} props
 * @param {Array} props.routes Route table (`routes.js`).
 * @param {boolean} [props.collapsed=false] Icon-only rail with tooltips (xl+ only).
 */
export function SidebarLinks({ routes, collapsed = false }) {
  const location = useLocation();
  const { user } = useAuth();
  const isPathway = user?.customerStatus === "pathway";

  const topLevel = routes.filter((route) => !route.hideInSidebar);

  const matches = (route, end) =>
    matchPath({ path: toPath(route), end }, location.pathname) !== null;

  // A module is active when its own path or any child route matches.
  const isActive = (route) =>
    matches(route, false) || (route.items || []).some((item) => isActive(item));

  // A child page is active on an exact match, or as a prefix when it is not the
  // module's index page (e.g. "Profiles" stays active on a profile detail).
  const isChildActive = (child, parent) =>
    matches(child, true) || (child.path !== parent.path && matches(child, false));

  // Only the active module is expanded after a navigation.
  const activeExpansion = () => {
    const next = {};
    topLevel.forEach((route) => {
      if (visibleItems(route).length > 0) next[route.name] = isActive(route);
    });
    return next;
  };

  const [expandedMenus, setExpandedMenus] = useState(activeExpansion);

  useEffect(() => {
    setExpandedMenus((prev) => ({ ...prev, ...activeExpansion() }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleMenu = (name) =>
    setExpandedMenus((prev) => ({ ...prev, [name]: !prev[name] }));

  const renderChild = (child, parent) => {
    const active = isChildActive(child, parent);
    return (
      <li key={toPath(child)}>
        <Link
          to={toPath(child)}
          aria-current={matches(child, true) ? "page" : undefined}
          className={`focus-ring block truncate rounded-control px-3 py-1.5 text-sm transition-colors ${
            active
              ? "bg-surface-subtle font-semibold text-fg"
              : "font-medium text-fg-muted hover:bg-surface-subtle hover:text-fg"
          }`}
        >
          {child.name}
        </Link>
      </li>
    );
  };

  const renderLocked = (route) => (
    <li key={route.name}>
      <div
        aria-disabled="true"
        title="Upgrade to unlock this feature"
        className={`flex cursor-not-allowed items-center rounded-control text-sm font-medium text-fg-muted opacity-40 ${
          collapsed ? "mx-auto h-10 w-10 justify-center" : "gap-3 px-3 py-2"
        }`}
      >
        {route.icon}
        {collapsed ? <span className="sr-only">{route.name} (locked)</span> : route.name}
      </div>
    </li>
  );

  const renderCollapsedItem = (route) => {
    const active = isActive(route);
    return (
      <li key={route.name} className="flex justify-center">
        <Tooltip
          placement="right"
          content={route.name}
          trigger={
            <Link
              to={toPath(route)}
              aria-label={route.name}
              aria-current={matches(route, true) ? "page" : undefined}
              className={`focus-ring flex h-10 w-10 items-center justify-center rounded-control transition-colors ${
                active
                  ? "bg-surface-subtle text-primary dark:text-accent"
                  : "text-fg-muted hover:bg-surface-subtle hover:text-fg"
              }`}
            >
              {route.icon}
            </Link>
          }
        />
      </li>
    );
  };

  const renderItem = (route) => {
    const active = isActive(route);
    const children = visibleItems(route);
    const isCollapsible = route.collapse && children.length > 0;
    const isExpanded = !!expandedMenus[route.name];
    const submenuId = `sidebar-submenu-${slug(route.name)}`;

    return (
      <li key={route.name}>
        <div
          className={`group flex items-center rounded-control transition-colors ${
            active ? "bg-surface-subtle" : "hover:bg-surface-subtle"
          }`}
        >
          <Link
            to={toPath(route)}
            aria-current={matches(route, true) ? "page" : undefined}
            onClick={() => {
              if (isCollapsible && !isExpanded) toggleMenu(route.name);
            }}
            className="focus-ring flex min-w-0 flex-1 items-center gap-3 rounded-control px-3 py-2 text-sm"
          >
            <span
              className={`flex shrink-0 ${
                active ? "text-primary dark:text-accent" : "text-fg-muted group-hover:text-fg"
              }`}
            >
              {route.icon}
            </span>
            <span
              className={`truncate ${
                active ? "font-semibold text-fg" : "font-medium text-fg-muted group-hover:text-fg"
              }`}
            >
              {route.name}
            </span>
          </Link>
          {isCollapsible && (
            <button
              type="button"
              aria-expanded={isExpanded}
              aria-controls={submenuId}
              aria-label={`${route.name} pages`}
              onClick={() => toggleMenu(route.name)}
              className="focus-ring mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-line/50 hover:text-fg"
            >
              <MdChevronRight
                aria-hidden="true"
                className={`h-4 w-4 transition-transform duration-200 ${
                  isExpanded ? "rotate-90" : ""
                }`}
              />
            </button>
          )}
        </div>
        {children.length > 0 && (
          <CollapsibleChildren id={submenuId} isOpen={!isCollapsible || isExpanded}>
            <ul className="flex flex-col gap-0.5 pb-1 pl-8 pt-0.5">
              {children.map((child) => renderChild(child, route))}
            </ul>
          </CollapsibleChildren>
        )}
      </li>
    );
  };

  const renderRoute = (route) => {
    if (isPathway && !PATHWAY_ALLOWED_ROUTES.includes(route.name)) return renderLocked(route);
    return collapsed ? renderCollapsedItem(route) : renderItem(route);
  };

  return (
    <div className="flex flex-col gap-4">
      {groupBySection(topLevel).map(({ section, routes: sectionRoutes }, index) => {
        const labelId = section ? `sidebar-section-${slug(section)}` : undefined;
        return (
          <div key={section || `group-${index}`}>
            {section &&
              (collapsed ? (
                index > 0 && <div aria-hidden="true" className="mx-5 mb-3 h-px bg-line" />
              ) : (
                <p
                  id={labelId}
                  className="mb-1 px-6 text-2xs font-semibold uppercase tracking-wider text-fg-muted"
                >
                  {section}
                </p>
              ))}
            <ul
              aria-labelledby={collapsed ? undefined : labelId}
              aria-label={collapsed ? section : undefined}
              className="flex flex-col gap-0.5 px-3"
            >
              {sectionRoutes.map(renderRoute)}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

export default SidebarLinks;
